'use client'

/**
 * Purpose: Skin-type summary + radar chart ("you vs. average") rendered after survey scoring
 * on /products, /routine and the onboarding verification step.
 * Inputs: `skinType` (4-letter code), `scores` (raw axis scores), `labels` (caller-translated
 * copy), optional `retake` CTA and optional `announcement` — translated text to read out through
 * a polite live region when the panel appears as the result of async scoring.
 * Outputs: A two-column panel; the chart is `aria-hidden` with an `sr-only` textual equivalent.
 */
import './skinResultsPanel.css'
import { ResultAnnouncer } from '@components/announcers/ResultAnnouncer/ResultAnnouncer'
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { AXIS_CONFIG, SKIN_TYPES, normalizeAxisScore, type AxisKey } from '@lib/survey-constants'
import HoverGradientButton from '@components/buttons/HoverGradientButton/HoverGradientButton'

export { SKIN_TYPES }

const AXIS_KEYS: AxisKey[] = ['oilyDry', 'sensitiveResistant', 'pigmented', 'wrinkled']

const AVERAGE: Record<AxisKey, number> = {
  oilyDry: normalizeAxisScore(AXIS_CONFIG.oilyDry.threshold, 'oilyDry'),
  sensitiveResistant: normalizeAxisScore(AXIS_CONFIG.sensitiveResistant.threshold, 'sensitiveResistant'),
  pigmented: normalizeAxisScore(AXIS_CONFIG.pigmented.threshold, 'pigmented'),
  wrinkled: normalizeAxisScore(AXIS_CONFIG.wrinkled.threshold, 'wrinkled'),
}

/** TODO move to types */
type Scores = {
  oilyDry: number
  sensitiveResistant: number
  pigmented: number
  wrinkled: number
}

/** All copy is translated by the caller and passed in, keeping this component free of next-intl. */
type Labels = {
  yourSkinType: string
  summaryTitle: string
  summaryDescription: string
  yourProfileVsAverage: string
  legendYou: string
  legendAverage: string
  axisOilyDry: string
  axisSensitiveResistant: string
  axisPigmented: string
  axisWrinkled: string
}

/** Optional link rendered as a filled HoverGradientButton under the summary. */
type RetakeCta = {
  href: string
  label: string
}

export function SkinResultsPanel({
  skinType,
  scores,
  labels,
  retake,
  announcement,
}: {
  skinType: string
  scores: Scores
  labels: Labels
  /** Optional "retake assessment" CTA rendered under the summary text. Omitted by default. */
  retake?: RetakeCta
  /**
   * Optional translated announcement (e.g. `skinResultsPanel.resultsAnnouncement`) read through a
   * polite live region when the panel appears after scoring. Omit on pages where the panel is
   * static content so screen-reader users are not interrupted on every visit.
   */
  announcement?: string
}) {
  // Explicit switches keep the axis → label/average/score lookups type-safe (no index access).
  const getAxisLabel = (axis: AxisKey): string => {
    switch (axis) {
      case 'oilyDry':
        return labels.axisOilyDry
      case 'sensitiveResistant':
        return labels.axisSensitiveResistant
      case 'pigmented':
        return labels.axisPigmented
      case 'wrinkled':
        return labels.axisWrinkled
    }
  }

  const getAverage = (axis: AxisKey): number => {
    switch (axis) {
      case 'oilyDry':
        return AVERAGE.oilyDry
      case 'sensitiveResistant':
        return AVERAGE.sensitiveResistant
      case 'pigmented':
        return AVERAGE.pigmented
      case 'wrinkled':
        return AVERAGE.wrinkled
    }
  }

  // Raw axis score for the user; normalised to 0–100 when building radarData below.
  const getScore = (axis: AxisKey): number => {
    switch (axis) {
      case 'oilyDry':
        return scores.oilyDry
      case 'sensitiveResistant':
        return scores.sensitiveResistant
      case 'pigmented':
        return scores.pigmented
      case 'wrinkled':
        return scores.wrinkled
    }
  }

  const radarData = AXIS_KEYS.map((key) => ({
    axis: getAxisLabel(key),
    you: normalizeAxisScore(getScore(key), key),
    average: getAverage(key),
  }))

  return (
    <div id="skin-results-panel" className="skin-results-panel">
      {/* Polite live region — only when the caller flags this render as a scoring result */}
      {announcement !== undefined && (
        <ResultAnnouncer id="skin-results-panel-announcer" message={announcement} />
      )}

      {/* Summary — left */}
      <div className="skin-results-body">
        <p className="skin-results-label">
          {labels.yourSkinType}
        </p>
        <h2 className="mb-1 tracking-tight">{skinType}</h2>
        <h3 className="mb-4 text-lavander">
          {labels.summaryTitle}
        </h3>
        <p className="text-black text-sm leading-relaxed">{labels.summaryDescription}</p>
        {retake && (
          <HoverGradientButton variant="filled" href={retake.href} className="self-start mt-4">
            {retake.label}
          </HoverGradientButton>
        )}
      </div>

      {/* Radar chart — right */}
      <div className="skin-results-chart-wrapper">
        <p className="skin-results-chart-label">
          {labels.yourProfileVsAverage}
        </p>
        {/* Text equivalent of the chart for screen readers; the SVG below is aria-hidden */}
        <p className="sr-only">
          {radarData
            .map((d) => `${d.axis} — ${labels.legendYou}: ${d.you}/100, ${labels.legendAverage}: ${d.average}/100`)
            .join('; ')}
        </p>
        <div aria-hidden="true">
        <ResponsiveContainer width="100%" height={320}>
          <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
            <PolarGrid stroke="#00000020" />
            <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
            <PolarAngleAxis
              dataKey="axis"
              tick={{ fill: '#000000', fontSize: 11 }}
            />
            {/* Dashed lavender = population average; solid orange = this user */}
            <Radar
              name={labels.legendAverage}
              dataKey="average"
              stroke="#beb6f1"
              fill="#beb6f1"
              fillOpacity={0.15}
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
            <Radar
              name={labels.legendYou}
              dataKey="you"
              stroke="#e75018"
              fill="#e75018"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            {/* 12px is the visual-standards floor for chart text */}
            <Legend
              wrapperStyle={{ fontSize: '12px', color: '#000000', paddingTop: '8px' }}
            />
          </RadarChart>
        </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
